/**
 * Time Series Chart Component
 *
 * Interactive chart for visualizing time series data with zoom, pan,
 * and multiple chart types (line, bar, area).
 */

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  CartesianGrid,
  ReferenceArea,
} from 'recharts';
import type { TimeSeries } from '@/simulation/parsers/types';
import type { AggregationFrequency } from '../utils/aggregation';
import { aggregateTimeSeries, getSuggestedAggregationMethod } from '../utils/aggregation';
import type { UnitSystem } from '../utils/unitConversion';
import { convertValue, getDisplayUnit, formatNumber, getUnitCategory } from '../utils/unitConversion';
import { exportChartAsPNG, multipleTimeSeriesToCSV, downloadFile } from '../utils/export';

/**
 * Chart type options
 */
type ChartType = 'line' | 'bar' | 'area' | 'stacked-area';

/**
 * Series configuration
 */
interface SeriesConfig {
  id: string;
  timeSeries: TimeSeries;
  name: string;
  color: string;
  visible: boolean;
}

/**
 * Chart data point
 */
interface ChartDataPoint {
  name: string;
  [key: string]: string | number;
}

/**
 * Default colors for chart series
 */
const SERIES_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
];

/**
 * Props for TimeSeriesChart component
 */
interface TimeSeriesChartProps {
  /** Array of time series to display */
  series: SeriesConfig[];
  /** Unit system (SI or IP) */
  unitSystem?: UnitSystem;
  /** Initial chart type */
  initialChartType?: ChartType;
  /** Initial aggregation frequency */
  initialFrequency?: AggregationFrequency;
  /** Chart title */
  title?: string;
  /** Chart height in pixels */
  height?: number;
  /** Show brush for zoom */
  showBrush?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback when series visibility changes */
  onSeriesToggle?: (seriesId: string, visible: boolean) => void;
}

/**
 * Custom tooltip component
 */
function ChartTooltip({
  active,
  payload,
  label,
  series,
  unitSystem,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
  series: SeriesConfig[];
  unitSystem: UnitSystem;
}) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
      <p className="font-medium text-foreground mb-2">{label}</p>
      {payload.map((entry) => {
        const seriesConfig = series.find((s) => s.id === entry.dataKey);
        const unit = seriesConfig?.timeSeries.variable.units ?? '';
        const displayUnit = getDisplayUnit(unit, unitSystem);

        return (
          <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">
              {seriesConfig?.name ?? entry.dataKey}:
            </span>
            <span className="text-foreground font-medium">
              {formatNumber(entry.value, 2)} {displayUnit}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Time Series Chart Component
 */
export function TimeSeriesChart({
  series,
  unitSystem = 'SI',
  initialChartType = 'line',
  initialFrequency = 'monthly',
  title = 'Time Series',
  height = 350,
  showBrush = true,
  className = '',
  onSeriesToggle,
}: TimeSeriesChartProps) {
  const [chartType, setChartType] = useState<ChartType>(initialChartType);
  const [frequency, setFrequency] = useState<AggregationFrequency>(initialFrequency);
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Zoom state
  const [zoomLeft, setZoomLeft] = useState<string | null>(null);
  const [zoomRight, setZoomRight] = useState<string | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomDomain, setZoomDomain] = useState<{ left: number; right: number } | null>(null);

  // Prepare chart data
  const chartData = useMemo(() => {
    const visibleSeries = series.filter((s) => s.visible);
    if (visibleSeries.length === 0) return [];

    // Aggregate each series
    const aggregatedSeries = visibleSeries.map((s) => ({
      id: s.id,
      data: aggregateTimeSeries(
        s.timeSeries.data,
        frequency,
        getSuggestedAggregationMethod(s.timeSeries.variable.units)
      ),
      units: s.timeSeries.variable.units,
    }));

    // Build combined data points
    const dataMap = new Map<string, ChartDataPoint>();

    for (const s of aggregatedSeries) {
      for (const point of s.data) {
        let entry = dataMap.get(point.label);
        if (!entry) {
          entry = { name: point.label };
          dataMap.set(point.label, entry);
        }
        // Convert to target unit system
        entry[s.id] = convertValue(point.value, s.units, unitSystem);
      }
    }

    // Sort by label (assuming labels are sortable like "Jan", "Feb", etc.)
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return Array.from(dataMap.values()).sort((a, b) => {
      const aIdx = monthOrder.indexOf(a.name);
      const bIdx = monthOrder.indexOf(b.name);
      if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
      return a.name.localeCompare(b.name);
    });
  }, [series, frequency, unitSystem]);

  // Group series by unit category for dual-axis support
  const unitGroups = useMemo(() => {
    const visibleSeries = series.filter((s) => s.visible);
    const groups = new Map<string, { category: string; units: string; seriesIds: string[] }>();

    for (const s of visibleSeries) {
      const units = s.timeSeries.variable.units;
      const category = getUnitCategory(units);

      if (!groups.has(category)) {
        groups.set(category, { category, units, seriesIds: [] });
      }
      groups.get(category)!.seriesIds.push(s.id);
    }

    return Array.from(groups.values());
  }, [series]);

  // Get display units for each axis (max 2)
  const axisConfig = useMemo(() => {
    const leftGroup = unitGroups[0];
    const rightGroup = unitGroups[1];

    return {
      left: leftGroup
        ? {
            category: leftGroup.category,
            displayUnit: getDisplayUnit(leftGroup.units, unitSystem),
            seriesIds: new Set(leftGroup.seriesIds),
          }
        : null,
      right: rightGroup
        ? {
            category: rightGroup.category,
            displayUnit: getDisplayUnit(rightGroup.units, unitSystem),
            seriesIds: new Set(rightGroup.seriesIds),
          }
        : null,
    };
  }, [unitGroups, unitSystem]);

  // Get yAxisId for a series
  const getYAxisId = (seriesId: string): 'left' | 'right' => {
    if (axisConfig.right?.seriesIds.has(seriesId)) {
      return 'right';
    }
    return 'left';
  };

  // Zoom handlers
  const handleMouseDown = useCallback((e: { activeLabel?: string }) => {
    if (e.activeLabel) {
      setZoomLeft(e.activeLabel);
      setIsZooming(true);
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: { activeLabel?: string }) => {
      if (isZooming && e.activeLabel) {
        setZoomRight(e.activeLabel);
      }
    },
    [isZooming]
  );

  const handleMouseUp = useCallback(() => {
    if (isZooming && zoomLeft && zoomRight) {
      const leftIdx = chartData.findIndex((d) => d.name === zoomLeft);
      const rightIdx = chartData.findIndex((d) => d.name === zoomRight);
      if (leftIdx >= 0 && rightIdx >= 0 && leftIdx !== rightIdx) {
        setZoomDomain({
          left: Math.min(leftIdx, rightIdx),
          right: Math.max(leftIdx, rightIdx),
        });
      }
    }
    setIsZooming(false);
    setZoomLeft(null);
    setZoomRight(null);
  }, [isZooming, zoomLeft, zoomRight, chartData]);

  const resetZoom = useCallback(() => {
    setZoomDomain(null);
  }, []);

  // Filter data based on zoom
  const displayData = useMemo(() => {
    if (!zoomDomain) return chartData;
    return chartData.slice(zoomDomain.left, zoomDomain.right + 1);
  }, [chartData, zoomDomain]);

  // Export handlers
  const handleExportPNG = async () => {
    if (chartRef.current) {
      setExportError(null);
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      try {
        await exportChartAsPNG(chartRef.current, `${title.replace(/\s+/g, '-').toLowerCase()}.png`);
      } catch (error) {
        console.error('Failed to export PNG:', error);
        const message = error instanceof Error ? error.message : 'Unknown error';
        setExportError(`Failed to export PNG: ${message}`);
        // Auto-clear error after 5 seconds
        errorTimeoutRef.current = setTimeout(() => { setExportError(null); }, 5000);
      }
    }
  };

  const handleExportCSV = () => {
    const csv = multipleTimeSeriesToCSV(
      series.filter((s) => s.visible).map((s) => ({ name: s.name, timeSeries: s.timeSeries })),
      frequency,
      { unitSystem }
    );
    downloadFile(csv, `${title.replace(/\s+/g, '-').toLowerCase()}.csv`);
  };

  // Toggle series visibility
  const handleLegendClick = (dataKey: string) => {
    const s = series.find((x) => x.id === dataKey);
    if (s && onSeriesToggle) {
      onSeriesToggle(s.id, !s.visible);
    }
  };

  // Render the appropriate chart type
  const renderChart = () => {
    const visibleSeries = series.filter((s) => s.visible);

    const gridProps = {
      strokeDasharray: '3 3',
      stroke: '#374151',
    };

    const xAxisProps = {
      dataKey: 'name' as const,
      tick: { fill: '#9ca3af', fontSize: 12 },
      axisLine: { stroke: '#4b5563' },
    };

    const leftYAxisProps = {
      yAxisId: 'left' as const,
      tick: { fill: '#9ca3af', fontSize: 12 },
      axisLine: { stroke: '#4b5563' },
      tickFormatter: (value: number) => formatNumber(value, 0),
      orientation: 'left' as const,
      ...(axisConfig.left?.displayUnit ? {
        label: { value: axisConfig.left.displayUnit, angle: -90, position: 'insideLeft' as const, fill: '#9ca3af', fontSize: 12 }
      } : {}),
    };

    const rightYAxisProps = axisConfig.right ? {
      yAxisId: 'right' as const,
      tick: { fill: '#9ca3af', fontSize: 12 },
      axisLine: { stroke: '#4b5563' },
      tickFormatter: (value: number) => formatNumber(value, 0),
      orientation: 'right' as const,
      ...(axisConfig.right.displayUnit ? {
        label: { value: axisConfig.right.displayUnit, angle: 90, position: 'insideRight' as const, fill: '#9ca3af', fontSize: 12 }
      } : {}),
    } : null;

    const tooltipProps = {
      content: <ChartTooltip series={series} unitSystem={unitSystem} />,
    };

    const handleLegendClickEvent = (data: unknown) => {
      const entry = data as { dataKey?: string } | null;
      if (entry?.dataKey) {
        handleLegendClick(entry.dataKey);
      }
    };

    const legendProps = {
      onClick: handleLegendClickEvent,
      formatter: (value: string) => {
        const s = series.find((x) => x.id === value);
        return <span className="text-muted-foreground text-sm cursor-pointer">{s?.name ?? value}</span>;
      },
    };

    const handleChartMouseDown = (e: unknown) => {
      const event = e as { activeLabel?: string | number } | null;
      if (event?.activeLabel !== undefined) {
        handleMouseDown({ activeLabel: String(event.activeLabel) });
      }
    };

    const handleChartMouseMove = (e: unknown) => {
      const event = e as { activeLabel?: string | number } | null;
      if (event?.activeLabel !== undefined) {
        handleMouseMove({ activeLabel: String(event.activeLabel) });
      }
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart
            data={displayData}
            onMouseDown={handleChartMouseDown}
            onMouseMove={handleChartMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid {...gridProps} />
            <XAxis {...xAxisProps} />
            <YAxis {...leftYAxisProps} />
            {rightYAxisProps && <YAxis {...rightYAxisProps} />}
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {visibleSeries.map((s) => (
              <Line
                key={s.id}
                type="monotone"
                dataKey={s.id}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={{ fill: s.color, r: 3 }}
                activeDot={{ r: 5 }}
                yAxisId={getYAxisId(s.id)}
              />
            ))}
            {showBrush && chartData.length > 10 && !zoomDomain && (
              <Brush
                dataKey="name"
                height={30}
                stroke="#4b5563"
                fill="#1f2937"
                tickFormatter={() => ''}
              />
            )}
            {isZooming && zoomLeft && zoomRight && (
              <ReferenceArea
                x1={zoomLeft}
                x2={zoomRight}
                strokeOpacity={0.3}
                fill="#3b82f6"
                fillOpacity={0.3}
                yAxisId="left"
              />
            )}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart
            data={displayData}
            onMouseDown={handleChartMouseDown}
            onMouseMove={handleChartMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid {...gridProps} />
            <XAxis {...xAxisProps} />
            <YAxis {...leftYAxisProps} />
            {rightYAxisProps && <YAxis {...rightYAxisProps} />}
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {visibleSeries.map((s) => (
              <Bar key={s.id} dataKey={s.id} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} yAxisId={getYAxisId(s.id)} />
            ))}
            {showBrush && chartData.length > 10 && !zoomDomain && (
              <Brush
                dataKey="name"
                height={30}
                stroke="#4b5563"
                fill="#1f2937"
                tickFormatter={() => ''}
              />
            )}
          </BarChart>
        );

      case 'area':
      case 'stacked-area':
        return (
          <AreaChart
            data={displayData}
            onMouseDown={handleChartMouseDown}
            onMouseMove={handleChartMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid {...gridProps} />
            <XAxis {...xAxisProps} />
            <YAxis {...leftYAxisProps} />
            {rightYAxisProps && <YAxis {...rightYAxisProps} />}
            <Tooltip {...tooltipProps} />
            <Legend {...legendProps} />
            {visibleSeries.map((s) => (
              <Area
                key={s.id}
                type="monotone"
                dataKey={s.id}
                name={s.name}
                stroke={s.color}
                fill={s.color}
                fillOpacity={chartType === 'stacked-area' ? 0.6 : 0.3}
                yAxisId={getYAxisId(s.id)}
                {...(chartType === 'stacked-area' ? { stackId: getYAxisId(s.id) } : {})}
              />
            ))}
            {showBrush && chartData.length > 10 && !zoomDomain && (
              <Brush
                dataKey="name"
                height={30}
                stroke="#4b5563"
                fill="#1f2937"
                tickFormatter={() => ''}
              />
            )}
          </AreaChart>
        );

      default:
        return null;
    }
  };

  if (series.length === 0) {
    return (
      <div className={`bg-card rounded-lg p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
        <div className="text-center text-muted-foreground py-8">No data to display</div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>

        <div className="flex flex-wrap items-center gap-2">
          {/* Chart type toggle */}
          <div className="flex bg-secondary rounded-lg p-1">
            {(['line', 'bar', 'area', 'stacked-area'] as ChartType[]).map((type) => (
              <button
                key={type}
                onClick={() => { setChartType(type); }}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                  chartType === type
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {type === 'stacked-area' ? 'Stack' : type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Frequency toggle */}
          <select
            value={frequency}
            onChange={(e) => { setFrequency(e.target.value as AggregationFrequency); }}
            className="px-2 py-1 text-sm bg-secondary text-muted-foreground rounded border border-border focus:outline-none focus:border-blue-500"
          >
            <option value="timestep">Timestep</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>

          {/* Zoom reset */}
          {zoomDomain && (
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Reset Zoom
            </button>
          )}

          {/* Export buttons */}
          <button
            onClick={handleExportCSV}
            className="px-2 py-1 text-sm bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors"
            title="Export as CSV"
          >
            CSV
          </button>
          <button
            onClick={handleExportPNG}
            className="px-2 py-1 text-sm bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors"
            title="Export as PNG"
          >
            PNG
          </button>
        </div>
      </div>

      {/* Export Error Message */}
      {exportError && (
        <div className="mb-3 px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-md flex items-center justify-between">
          <span>{exportError}</span>
          <button
            onClick={() => { setExportError(null); }}
            className="ml-2 text-destructive hover:text-destructive/80"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Chart */}
      <div ref={chartRef} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Zoom hint */}
      {!zoomDomain && chartData.length > 5 && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Click and drag on the chart to zoom in
        </p>
      )}
    </div>
  );
}

export { SERIES_COLORS };
export default TimeSeriesChart;
